import React, {useCallback, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {submitReviewButtonsSelector} from "./submitReviewButtons.selector";
import {reviewActions} from "../review.slice";
import {confirm} from "../../utils/confirm"
import { styled } from "styled-components";
import { ThemeProps } from "../../styles/theme";
import {DifficultyLevel} from "../../common/api";

const MINUTES_PER_DAY = 24 * 60;

const Root = styled.div<ThemeProps>`
    background-color: ${props => props.theme.secondaryBackground};
    border: ${props => props.theme.secondaryBorder} 1px solid;
    padding: 20px;
`;

const Line = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;

    &:last-child {
        margin-top: 20px;
    }
`;

const Button = styled.button<ThemeProps>`
    font-size: 1em;
    width: calc(50% - 10px);
    box-sizing: border-box;
    background-color: ${props => props.theme.neutralButtonBackground};
    color: ${props => props.theme.neutralButtonForeground};
    outline: none;
    border: ${props => props.theme.neutralButtonBorder} 1px solid;

    &:focus {
        background-color: ${props => props.theme.neutralButtonBorder};
    }
`;

const SuccessButton = styled(Button)<ThemeProps>`
    background-color: ${props => props.theme.successButtonBackground};
    color: ${props => props.theme.successButtonForeground};
    outline: none;
    border: ${props => props.theme.successButtonBorder} 1px solid;

    &:focus {
        background-color: ${props => props.theme.successButtonBorder};
    }
`;

const FailButton = styled(Button)`
    background-color: ${props => props.theme.failButtonBackground};
    color: ${props => props.theme.failButtonForeground};
    outline: none;
    border: ${props => props.theme.failButtonBorder} 1px solid;

    &:focus {
        background-color: ${props => props.theme.failButtonBorder};
    }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const DialogContainer = styled.div<ThemeProps>`
    background-color: ${props => props.theme.primaryBackground};
    border-radius: 8px;
    padding: 20px;
    width: 300px;
    max-width: 90%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const DialogTitle = styled.h3<ThemeProps>`
    color: ${props => props.theme.primaryText};
    margin-top: 0;
    margin-bottom: 16px;
`;

const DaysInput = styled.input<ThemeProps>`
    width: 100%;
    box-sizing: border-box;
    padding: 8px;
    font-size: 1em;
    margin-bottom: 20px;
    border-radius: 4px;
    border: ${props => props.theme.neutralButtonBorder} 1px solid;
    background-color: ${props => props.theme.secondaryBackground};
    color: ${props => props.theme.primaryText};
`;

const DialogButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const DialogButton = styled.button<ThemeProps>`
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 1em;
    background-color: ${props => props.theme.neutralButtonBackground};
    color: ${props => props.theme.neutralButtonForeground};
    border: ${props => props.theme.neutralButtonBorder} 1px solid;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const ConfirmDialogButton = styled(DialogButton)`
    background-color: ${props => props.theme.successButtonBackground};
    color: ${props => props.theme.successButtonForeground};
    border: ${props => props.theme.successButtonBorder} 1px solid;
`;

export const SubmitReviewButtons: React.FC = React.memo(() => {
    const dispatch = useDispatch();
    const stateProps = useSelector(submitReviewButtonsSelector);

    const [isCustomOpen, setIsCustomOpen] = useState(false);
    const [customDays, setCustomDays] = useState('');

    const dispatchSubmit = useCallback((intervalMinutes: number, difficulty?: DifficultyLevel) => {
            dispatch(reviewActions.submit({
                intervalMinutes,
                difficulty
            }))
        },
        [dispatch]);

    const againCallback = useCallback(() => {
        if (confirm('Again?')) {
            dispatchSubmit(stateProps.again, 'again');
        }
    }, [dispatchSubmit, stateProps]);

    const openCustomCallback = useCallback(() => {
        setCustomDays(String(stateProps.defaultCustomDays));
        setIsCustomOpen(true);
    }, [stateProps]);

    const closeCustomCallback = useCallback(() => {
        setIsCustomOpen(false);
    }, []);

    const parsedDays = Math.floor(Number(customDays));
    const isCustomValid = customDays.trim() !== '' && Number.isFinite(parsedDays) && parsedDays >= 1;

    const confirmCustomCallback = useCallback(() => {
        if (!isCustomValid) {
            return;
        }
        dispatchSubmit(parsedDays * MINUTES_PER_DAY, 'custom');
        setIsCustomOpen(false);
    }, [dispatchSubmit, isCustomValid, parsedDays]);

    const customKeyDownCallback = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            confirmCustomCallback();
        }
    }, [confirmCustomCallback]);

    const hardCallback = useCallback(() => {
        if (confirm('Hard?')) {
            dispatchSubmit(stateProps.hard, 'hard');
        }
    }, [dispatchSubmit, stateProps]);

    const goodCallback = useCallback(() => {
        dispatchSubmit(stateProps.good, 'good');
    }, [dispatchSubmit, stateProps]);


    return (
        <Root>
            <Line>
                <FailButton data-testid="review-rate-again" onClick={againCallback}>Again, {stateProps.formattedAgain}</FailButton>
                <SuccessButton data-testid="review-rate-good" onClick={goodCallback}>Good, {stateProps.formattedGood}</SuccessButton>
            </Line>
            <Line>
                <Button data-testid="review-rate-hard" onClick={hardCallback}>Hard, {stateProps.formattedHard}</Button>
                <Button data-testid="review-rate-custom" onClick={openCustomCallback}>Custom…</Button>
            </Line>
            {isCustomOpen && (
                <Overlay onClick={closeCustomCallback}>
                    <DialogContainer onClick={e => e.stopPropagation()}>
                        <DialogTitle>Next review in (days)</DialogTitle>
                        <DaysInput
                            type="number"
                            min={1}
                            step={1}
                            value={customDays}
                            autoFocus
                            onFocus={e => e.target.select()}
                            onChange={e => setCustomDays(e.target.value)}
                            onKeyDown={customKeyDownCallback}
                        />
                        <DialogButtons>
                            <DialogButton onClick={closeCustomCallback}>Cancel</DialogButton>
                            <ConfirmDialogButton onClick={confirmCustomCallback} disabled={!isCustomValid}>
                                Confirm
                            </ConfirmDialogButton>
                        </DialogButtons>
                    </DialogContainer>
                </Overlay>
            )}
        </Root>
    );
});
